import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Loader2, AlertTriangle, CheckCircle, Search, User, Calendar, 
  Ticket, DollarSign, ArrowRight, Info, Copy, Users, Save, RotateCcw
} from "lucide-react";
import CopyableText from "./CopyableText";
import ReportBugLink from "./ReportBugLink";

// Storage key for draft
const DRAFT_STORAGE_KEY = 'middleman_transfer_draft';

// Progress bar component for the transfer form
const ProgressBar = ({ currentStep, steps }) => {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between relative">
        {/* Background line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-10" />
        {/* Progress line */}
        <div 
          className="absolute top-4 left-0 h-0.5 bg-brand-500 transition-all duration-300 -z-10"
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
        
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          
          return (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                  isCompleted 
                    ? 'bg-brand-500 text-white' 
                    : isCurrent 
                      ? 'bg-brand-500 text-white ring-4 ring-brand-100' 
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isCompleted ? <CheckCircle className="h-4 w-4" /> : stepNumber}
              </div>
              <span className={`mt-1 text-xs font-medium hidden sm:block ${
                isCurrent ? 'text-brand-600' : isCompleted ? 'text-brand-500' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TransferRequestForm = ({ user, onSuccess, onCancel, prefillData = null, prefillSellerClientNumber = null }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [transferFee, setTransferFee] = useState(24.95); // Default, will be fetched from API
  const [feeType, setFeeType] = useState('fixed'); // 'fixed' or 'percentage'
  const [platforms, setPlatforms] = useState([]); // Dynamic platforms from API
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeValidating, setPromoCodeValidating] = useState(false);
  const [promoCodeResult, setPromoCodeResult] = useState(null);
  const [userPromoCode, setUserPromoCode] = useState(null); // User's personal promo code
  
  // Draft restoration state
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  
  // Client lookup states
  const [sellerClientNumber, setSellerClientNumber] = useState(prefillSellerClientNumber || "");
  const [buyerClientNumber, setBuyerClientNumber] = useState("");
  const [sellerInfo, setSellerInfo] = useState(null);
  const [buyerInfo, setBuyerInfo] = useState(null);
  const [sellerSearching, setSellerSearching] = useState(false);
  const [buyerSearching, setBuyerSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    creator_role: prefillData ? "buyer" : "", // If coming from listing, user is the BUYER
    event_name: prefillData?.event_name || "",
    event_date: prefillData?.event_date || "",
    event_time: "",
    venue: prefillData?.event_venue || "",
    ticket_count: prefillData?.ticket_count || 1,
    price: prefillData?.price_per_ticket ? String(prefillData.price_per_ticket * (prefillData.ticket_count || 1)) : "",
    currency: prefillData?.currency || "CAD",
    section: prefillData?.section || "",
    row: prefillData?.row || "",
    seats: prefillData?.seats || "",
    ticket_platform: "Ticketmaster",
    event_type: "concert",
    seat_type: "",
    special_section_details: "",
    fee_payer: "buyer",
    source_listing_slug: prefillData?.slug || null,
  });

  // Per-ticket details for "siege_assigne"
  const [ticketDetails, setTicketDetails] = useState([{ section: "", row: "", seat: "" }]);

  // Fetch current user info and transfer fee on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch user info
        const userResponse = await axios.get(`${API}/clients/me`);
        setCurrentUserInfo(userResponse.data);
        
        // Fetch current transfer fee settings
        const feeResponse = await axios.get(`${API}/transfer-fee`);
        if (feeResponse.data) {
          setFeeType(feeResponse.data.fee_type || 'fixed');
          if (feeResponse.data.fee_type === 'percentage') {
            setTransferFee(feeResponse.data.fee_value || 5);
          } else {
            setTransferFee(feeResponse.data.transfer_fee || 24.95);
          }
        }
        
        // Fetch platforms from API
        const platformsResponse = await axios.get(`${API}/platforms`);
        if (platformsResponse.data && platformsResponse.data.length > 0) {
          setPlatforms(platformsResponse.data);
          // Set default platform to the first one
          setFormData(prev => ({
            ...prev,
            ticket_platform: platformsResponse.data[0].name
          }));
        }
        
        // Fetch user's personal promo code if available
        try {
          const promoResponse = await axios.get(`${API}/auth/my-promo-code`);
          if (promoResponse.data.has_promo_code) {
            setUserPromoCode(promoResponse.data);
          }
        } catch (e) {
          console.log("No personal promo code available");
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Check for existing draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Check if draft is less than 24 hours old
        const draftAge = Date.now() - draft.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (draftAge < maxAge && draft.formData) {
          setHasDraft(true);
          setDraftTimestamp(draft.timestamp);
          setShowDraftPrompt(true);
        } else {
          // Draft too old, remove it
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      } catch (e) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
  }, []);

  // Auto-save draft when form data changes (debounced)
  useEffect(() => {
    if (!draftLoaded && showDraftPrompt) return; // Don't save while draft prompt is showing
    
    const saveTimer = setTimeout(() => {
      // Only save if there's meaningful data
      const hasData = formData.creator_role || formData.event_name || formData.price || 
                     sellerClientNumber || buyerClientNumber;
      
      if (hasData) {
        const draftData = {
          formData,
          sellerClientNumber,
          buyerClientNumber,
          ticketDetails,
          timestamp: Date.now()
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      }
    }, 1000); // Save after 1 second of inactivity
    
    return () => clearTimeout(saveTimer);
  }, [formData, sellerClientNumber, buyerClientNumber, ticketDetails, draftLoaded, showDraftPrompt]);

  // Function to restore draft
  const restoreDraft = () => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft.formData) setFormData(draft.formData);
        if (draft.sellerClientNumber) setSellerClientNumber(draft.sellerClientNumber);
        if (draft.buyerClientNumber) setBuyerClientNumber(draft.buyerClientNumber);
        if (draft.ticketDetails) setTicketDetails(draft.ticketDetails);
        toast.success("Brouillon restauré!");
      } catch (e) {
        toast.error("Erreur lors de la restauration du brouillon");
      }
    }
    setShowDraftPrompt(false);
    setDraftLoaded(true);
  };

  // Function to discard draft
  const discardDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setShowDraftPrompt(false);
    setHasDraft(false);
    setDraftLoaded(true);
    toast.info("Nouveau formulaire démarré");
  };

  // Function to clear draft (called on successful submission)
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
  };

  // Validate promo code
  const validatePromoCode = async (code) => {
    if (!code || code.trim().length < 5) {
      setPromoCodeResult(null);
      return;
    }
    
    const totalPrice = parseFloat(formData.price) || 0;
    if (totalPrice <= 0) {
      toast.error("Entrez d'abord le prix total des billets");
      return;
    }
    
    setPromoCodeValidating(true);
    try {
      const response = await axios.post(`${API}/transactions/validate-promo-code`, null, {
        params: {
          promo_code: code.trim(),
          amount: totalPrice,
          ticket_count: formData.ticket_count || 1,
          event_name: formData.event_name || ""
        }
      });
      
      setPromoCodeResult(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      }
    } catch (error) {
      setPromoCodeResult({
        valid: false,
        message: error.response?.data?.detail || "Code promo invalide"
      });
    } finally {
      setPromoCodeValidating(false);
    }
  };

  // Apply user's personal promo code
  const applyUserPromoCode = () => {
    if (userPromoCode?.promo_code) {
      setPromoCode(userPromoCode.promo_code);
      validatePromoCode(userPromoCode.promo_code);
    }
  };

  // Auto-fill client number when role is selected
  useEffect(() => {
    if (formData.creator_role && currentUserInfo?.client_number) {
      if (formData.creator_role === "seller") {
        setSellerClientNumber(currentUserInfo.client_number);
        setSellerInfo({ full_name: currentUserInfo.full_name, found: true });
      } else if (formData.creator_role === "buyer") {
        setBuyerClientNumber(currentUserInfo.client_number);
        setBuyerInfo({ full_name: currentUserInfo.full_name, found: true });
      }
    }
  }, [formData.creator_role, currentUserInfo]);

  // Sync ticket details array with ticket count
  useEffect(() => {
    const count = parseInt(formData.ticket_count) || 1;
    setTicketDetails(prev => {
      if (prev.length === count) return prev;
      if (prev.length < count) {
        return [...prev, ...Array(count - prev.length).fill(null).map(() => ({ section: "", row: "", seat: "" }))];
      }
      return prev.slice(0, count);
    });
  }, [formData.ticket_count]);

  // Search for client by number
  const searchClient = async (type) => {
    const clientNumber = type === "seller" ? sellerClientNumber : buyerClientNumber;
    const setSearching = type === "seller" ? setSellerSearching : setBuyerSearching;
    const setInfo = type === "seller" ? setSellerInfo : setBuyerInfo;
    
    if (!clientNumber || clientNumber.trim().length === 0) {
      setInfo(null);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/clients/lookup/${clientNumber.trim()}`);
      setInfo(response.data);
    } catch (error) {
      setInfo({ found: false, error: "Aucun client trouvé avec ce numéro" });
    } finally {
      setSearching(false);
    }
  };

  // Auto-search when client number changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (sellerClientNumber && sellerClientNumber.trim().length > 0) {
        if (formData.creator_role !== "seller") {
          searchClient("seller");
        }
      }
    }, 500); // Debounce 500ms
    return () => clearTimeout(timer);
  }, [sellerClientNumber, formData.creator_role]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (buyerClientNumber && buyerClientNumber.trim().length > 0) {
        if (formData.creator_role !== "buyer") {
          searchClient("buyer");
        }
      }
    }, 500); // Debounce 500ms
    return () => clearTimeout(timer);
  }, [buyerClientNumber, formData.creator_role]);

  // Copy email to clipboard
  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success("Courriel copié!");
  };

  // Confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [preparedData, setPreparedData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.creator_role) {
      toast.error("Veuillez indiquer si vous êtes le vendeur ou l'acheteur");
      return;
    }
    if (!sellerInfo?.found) {
      toast.error("Veuillez entrer un numéro de client vendeur valide");
      return;
    }
    if (!buyerInfo?.found) {
      toast.error("Veuillez entrer un numéro de client acheteur valide");
      return;
    }
    if (sellerClientNumber === buyerClientNumber) {
      toast.error("L'acheteur et le vendeur doivent être différents");
      return;
    }
    if (!formData.event_name) {
      toast.error("Le nom de l'événement est requis");
      return;
    }
    if (!formData.event_date) {
      toast.error("La date de l'événement est requise");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Le prix doit être supérieur à 0");
      return;
    }
    if (!formData.seat_type) {
      toast.error("Veuillez sélectionner un type de billet");
      return;
    }
    if (!formData.event_time) {
      toast.error("L'heure de l'événement est requise");
      return;
    }
    if (!formData.venue || formData.venue.trim().length < 2) {
      toast.error("Le lieu de l'événement est requis (même pour une admission générale)");
      return;
    }
    if (!formData.ticket_count || parseInt(formData.ticket_count) < 1) {
      toast.error("Le nombre de billets est requis");
      return;
    }
    if (!formData.ticket_platform) {
      toast.error("La plateforme des billets est requise");
      return;
    }
    if (!formData.event_type) {
      toast.error("Le type d'événement est requis");
      return;
    }
    if (formData.seat_type === "siege_assigne") {
      const incomplete = ticketDetails.some(t => !t.section || !t.row || !t.seat);
      if (incomplete) {
        toast.error("Veuillez remplir les détails de tous les billets (section, rangée, siège)");
        return;
      }
    }
    if (formData.seat_type === "zone_speciale" && !formData.special_section_details) {
      toast.error("Veuillez préciser la zone spéciale");
      return;
    }

    // Build section/row/seats from ticket details for siege_assigne
    let section = formData.section;
    let row = formData.row;
    let seats = formData.seats;
    let level = formData.level;
    let ticketDetailsToSend = [];
    
    if (formData.seat_type === "siege_assigne") {
      level = ticketDetails.map(t => t.level).filter(Boolean).join(", ");
      section = ticketDetails.map(t => t.section).filter(Boolean).join(", ");
      row = ticketDetails.map(t => t.row).filter(Boolean).join(", ");
      seats = ticketDetails.map(t => t.seat).filter(Boolean).join(", ");
      ticketDetailsToSend = ticketDetails.map(t => ({
        level: t.level || '',
        section: t.section || '',
        row: t.row || '',
        seat: t.seat || ''
      }));
    }

    setPreparedData({ level, section, row, seats, ticketDetails: ticketDetailsToSend });
    setShowConfirmation(true);
  };

  const handleConfirmAndSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/transactions`, {
        ...formData,
        seller_client_number: sellerClientNumber,
        buyer_client_number: buyerClientNumber,
        price: parseFloat(formData.price),
        ticket_count: parseInt(formData.ticket_count),
        level: preparedData.level,
        section: preparedData.section,
        row: preparedData.row,
        seats: preparedData.seats,
        ticket_details: preparedData.ticketDetails || [],
        promo_code: promoCodeResult?.valid ? promoCode.trim() : null, // Include promo code if valid
        source_listing_slug: formData.source_listing_slug || null,  // Link to listing if buyer came from one
      });
      
      // Clear draft on successful submission
      clearDraft();
      
      toast.success(`Demande de transfert créée! Référence: ${response.data.reference_number}`);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la demande");
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Fee calculation constants - based on TOTAL transfer amount
  const FEE_TIER_LOW = 6.95;      // For transfers $125 or less
  const FEE_TIER_MID = 9.95;      // For transfers $125.01 to $250
  const FEE_TIER_HIGH = 19.95;    // For transfers over $250
  const FEE_THRESHOLD_LOW = 125.00;   // Low tier threshold
  const FEE_THRESHOLD_HIGH = 250.00;  // High tier threshold

  // Calculate the fee based on TOTAL transfer amount
  const getFeeForPrice = (totalPrice) => {
    if (totalPrice <= FEE_THRESHOLD_LOW) {
      return FEE_TIER_LOW;
    } else if (totalPrice <= FEE_THRESHOLD_HIGH) {
      return FEE_TIER_MID;
    }
    return FEE_TIER_HIGH;
  };

  // Calculate fees preview using the tiered fee system
  const calculateFees = () => {
    const price = parseFloat(formData.price) || 0;
    const ticketCount = parseInt(formData.ticket_count) || 1;
    const fee = getFeeForPrice(price);
    
    // Determine currency based on seller's country
    const sellerCountry = sellerInfo?.country || 'CA';
    const europeanCountries = ['FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'IE', 'GR', 'FI', 'LU', 'MT', 'CY', 'SK', 'SI', 'EE', 'LV', 'LT'];
    
    let currencySymbol = 'CAD';
    let currencyDisplay = '$';
    if (europeanCountries.includes(sellerCountry)) {
      currencySymbol = 'EUR';
      currencyDisplay = '€';
    } else if (sellerCountry !== 'CA') {
      currencySymbol = 'USD';
      currencyDisplay = '$';
    }
    
    if (formData.fee_payer === "buyer") {
      return { buyerPays: price + fee, sellerReceives: price, fee, totalAmount: price, ticketCount, currencySymbol, currencyDisplay };
    } else if (formData.fee_payer === "seller") {
      return { buyerPays: price, sellerReceives: price - fee, fee, totalAmount: price, ticketCount, currencySymbol, currencyDisplay };
    } else {
      return { buyerPays: price + fee/2, sellerReceives: price - fee/2, fee, totalAmount: price, ticketCount, currencySymbol, currencyDisplay };
    }
  };

  const fees = calculateFees();

  // Progress steps definition
  const progressSteps = [
    { id: 'role', label: 'Rôle' },
    { id: 'parties', label: 'Parties' },
    { id: 'event', label: 'Événement' },
    { id: 'tickets', label: 'Billets' },
    { id: 'fees', label: 'Frais' }
  ];

  // Calculate current step based on form completion
  const calculateCurrentStep = () => {
    // Step 1: Role
    if (!formData.creator_role) return 1;
    
    // Step 2: Parties (seller and buyer)
    if (!sellerInfo?.found || !buyerInfo?.found) return 2;
    
    // Step 3: Event info
    if (!formData.event_name || !formData.event_date || !formData.event_time || !formData.venue) return 3;
    
    // Step 4: Ticket info
    if (!formData.seat_type || !formData.price || parseFloat(formData.price) <= 0) return 4;
    
    // Step 5: Fees (always reachable if previous steps are complete)
    return 5;
  };

  const currentStep = calculateCurrentStep();

  // Determine currency based on seller's country
  const getTransferFeeCurrency = () => {
    const sellerCountry = sellerInfo?.country || 'CA';
    
    // European countries
    const europeanCountries = ['FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'IE', 'GR', 'FI', 'LU', 'MT', 'CY', 'SK', 'SI', 'EE', 'LV', 'LT'];
    
    if (sellerCountry === 'CA') {
      return { symbol: 'CAD', display: '$' };
    } else if (europeanCountries.includes(sellerCountry)) {
      return { symbol: 'EUR', display: '€' };
    } else {
      return { symbol: 'USD', display: '$' };
    }
  };

  const feeCurrency = getTransferFeeCurrency();

  // Format fee display with the tiered pricing explanation
  const getFeeDisplayText = () => {
    const price = parseFloat(formData.price) || 0;
    const ticketCount = parseInt(formData.ticket_count) || 1;
    const currentFee = getFeeForPrice(price, ticketCount);
    return `${currentFee.toFixed(2)}$`;
  };

  // Get the fee tier explanation text
  const getFeeTierText = () => {
    const price = parseFloat(formData.price) || 0;
    if (price > 0) {
      if (price <= FEE_THRESHOLD_LOW) {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_LOW.toFixed(2)}$`;
      } else if (price <= FEE_THRESHOLD_HIGH) {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_MID.toFixed(2)}$`;
      } else {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_HIGH.toFixed(2)}$`;
      }
    }
    return `≤125$ = ${FEE_TIER_LOW.toFixed(2)}$ | 125-250$ = ${FEE_TIER_MID.toFixed(2)}$ | >250$ = ${FEE_TIER_HIGH.toFixed(2)}$`;
  };

  return (
    <div className="max-h-[70vh] overflow-y-auto px-1">
      {/* Draft Restoration Prompt */}
      {showDraftPrompt && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Save className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-800">Brouillon trouvé</h4>
              <p className="text-sm text-amber-700 mt-1">
                Vous avez un formulaire non terminé sauvegardé 
                {draftTimestamp && (
                  <span className="text-amber-600">
                    {" "}(il y a {Math.round((Date.now() - draftTimestamp) / 60000)} minutes)
                  </span>
                )}
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={restoreDraft}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="restore-draft-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reprendre
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={discardDraft}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="discard-draft-btn"
                >
                  Nouveau formulaire
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation View */}
      {showConfirmation ? (
        <div className="flex flex-col max-h-[70vh]">
          {/* Fixed Header */}
          <div className="text-center pb-4 border-b flex-shrink-0">
            <div className="flex items-center justify-center gap-2 text-brand-700 text-xl font-semibold">
              <Ticket className="h-6 w-6" />
              Confirmez vos informations
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Veuillez vérifier que toutes les informations sont exactes avant de soumettre
            </p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto py-4 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-500">Rôle:</span> <strong>{formData.creator_role === 'seller' ? 'Vendeur' : 'Acheteur'}</strong></div>
              <div><span className="text-gray-500">Événement:</span> <strong>{formData.event_name}</strong></div>
              <div><span className="text-gray-500">Date:</span> <strong>{formData.event_date}</strong></div>
              {formData.event_time && <div><span className="text-gray-500">Heure:</span> <strong>{formData.event_time}</strong></div>}
              {formData.venue && <div className="col-span-2"><span className="text-gray-500">Lieu:</span> <strong>{formData.venue}</strong></div>}
              <div><span className="text-gray-500">Vendeur:</span> <strong>{sellerClientNumber}</strong></div>
              <div><span className="text-gray-500">Acheteur:</span> <strong>{buyerClientNumber}</strong></div>
              <div><span className="text-gray-500">Billets:</span> <strong>{formData.ticket_count}</strong></div>
              <div><span className="text-gray-500">Prix total:</span> <strong>{parseFloat(formData.price).toFixed(2)}$</strong></div>
              <div><span className="text-gray-500">Type:</span> <strong>{formData.seat_type === 'admission_generale' ? 'Admission générale' : formData.seat_type === 'siege_assigne' ? 'Siège assigné' : 'Zone spéciale'}</strong></div>
              {formData.special_section_details && <div className="col-span-2"><span className="text-gray-500">Zone spéciale:</span> <strong>{formData.special_section_details}</strong></div>}
            </div>
            
            {/* Affichage des détails de billets groupés */}
            {preparedData?.ticketDetails && preparedData.ticketDetails.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-gray-600 font-medium mb-2">Détails des billets:</p>
                <div className="space-y-1">
                  {preparedData.ticketDetails.map((ticket, idx) => (
                    <div key={idx} className="text-sm font-medium text-gray-800">
                      Billet {idx + 1}: {[
                        ticket.level && `Niveau ${ticket.level}`,
                        ticket.section && `Section ${ticket.section}`,
                        ticket.row && `Rangée ${ticket.row}`,
                        ticket.seat && `Siège ${ticket.seat}`
                      ].filter(Boolean).join(' / ')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between"><span>Frais de transfert:</span><span>{fees.fee.toFixed(2)}$</span></div>
              <div className="flex justify-between text-green-700 font-semibold"><span>L'acheteur paie:</span><span>{fees.buyerPays.toFixed(2)}$</span></div>
              <div className="flex justify-between text-brand-700 font-semibold"><span>Le vendeur reçoit:</span><span>{fees.sellerReceives.toFixed(2)}$</span></div>
            </div>
          </div>

          {/* Fixed Footer Buttons */}
          <div className="flex gap-3 pt-4 border-t flex-shrink-0 bg-white">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
              data-testid="modify-transfer-btn"
            >
              Modifier les infos
            </Button>
            <Button
              type="button"
              className="flex-1 bg-brand-600 hover:bg-brand-700 h-12 text-base"
              onClick={handleConfirmAndSubmit}
              disabled={isLoading}
              data-testid="confirm-transfer-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                "Oui, confirmer"
              )}
            </Button>
          </div>
        </div>
      ) : (
      <div className="space-y-6">
        {/* Auto-save indicator */}
        <div className="flex items-center justify-center gap-1 text-xs text-gray-400 pb-2 border-b">
          <Save className="h-3 w-3" />
          <span>Sauvegarde automatique activée</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Step 1: Who are you? */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Vous êtes? *</Label>
              <RadioGroup
                value={formData.creator_role}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, creator_role: value }));
                  // Reset the other party's info when switching
                  if (value === "seller") {
                    setBuyerClientNumber("");
                    setBuyerInfo(null);
                  } else {
                    setSellerClientNumber("");
                    setSellerInfo(null);
                  }
                }}
                className="grid grid-cols-2 gap-4"
              >
                <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${formData.creator_role === 'seller' ? 'border-brand-500 bg-brand-50' : 'hover:bg-gray-50'}`}>
                  <RadioGroupItem value="seller" id="seller" />
                  <Label htmlFor="seller" className="cursor-pointer flex-1">
                    <span className="font-medium">Le vendeur</span>
                    <p className="text-xs text-gray-500">Je vends mes billets</p>
                  </Label>
                </div>
                <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${formData.creator_role === 'buyer' ? 'border-brand-500 bg-brand-50' : 'hover:bg-gray-50'}`}>
                  <RadioGroupItem value="buyer" id="buyer" />
                  <Label htmlFor="buyer" className="cursor-pointer flex-1">
                    <span className="font-medium">L'acheteur</span>
                    <p className="text-xs text-gray-500">J'achète des billets</p>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Step 2: Seller Client Number */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                Numéro de client du vendeur *
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="C26000001"
                  value={sellerClientNumber}
                  onChange={(e) => {
                    setSellerClientNumber(e.target.value);
                    setSellerInfo(null);
                  }}
                  disabled={formData.creator_role === "seller"}
                  className={formData.creator_role === "seller" ? "bg-gray-100" : ""}
                />
                {formData.creator_role !== "seller" && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => searchClient("seller")}
                    disabled={sellerSearching || !sellerClientNumber}
                  >
                    {sellerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                )}
              </div>
              {sellerInfo && (
                <div className={`flex items-center gap-2 p-2 rounded text-sm ${sellerInfo.found ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {sellerInfo.found ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Vendeur: <strong>{sellerInfo.full_name}</strong></span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4" />
                      <span>{sellerInfo.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: Buyer Client Number */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                Numéro de client de l'acheteur *
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="C26000002"
                  value={buyerClientNumber}
                  onChange={(e) => {
                    setBuyerClientNumber(e.target.value);
                    setBuyerInfo(null);
                  }}
                  disabled={formData.creator_role === "buyer"}
                  className={formData.creator_role === "buyer" ? "bg-gray-100" : ""}
                />
                {formData.creator_role !== "buyer" && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => searchClient("buyer")}
                    disabled={buyerSearching || !buyerClientNumber}
                  >
                    {buyerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                )}
              </div>
              {buyerInfo && (
                <div className={`flex items-center gap-2 p-2 rounded text-sm ${buyerInfo.found ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {buyerInfo.found ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Acheteur: <strong>{buyerInfo.full_name}</strong></span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4" />
                      <span>{buyerInfo.error}</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Invite other party note - shown under the field for the OTHER party */}
              {formData.creator_role && (
                <Alert className="bg-blue-50 border-blue-200 mt-3">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    <p className="mb-2">
                      {formData.creator_role === 'seller' 
                        ? "L'acheteur n'a pas de compte? Invitez-le à se créer un compte et à vous fournir son numéro de client."
                        : "Le vendeur n'a pas de compte? Invitez-le à se créer un compte et à vous fournir son numéro de client."}
                    </p>
                    <div className="flex items-center gap-2 bg-white rounded px-2 py-1.5 border border-blue-200">
                      <span className="text-xs font-mono flex-1 truncate">https://transfertdebillets.ca/auth</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                        onClick={() => {
                          navigator.clipboard.writeText('https://transfertdebillets.ca/auth');
                          toast.success('Lien copié!');
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Event Information */}
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-brand-600" />
                Informations sur l'événement
              </h3>

              {/* Warning about ticket info - moved here under the title */}
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Important:</strong> Les informations ci-dessous doivent correspondre exactement aux informations sur les billets. Toute information erronée pourrait retarder ou annuler le transfert.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label>Nom de l'événement *</Label>
                <Input
                  placeholder="Ex: Coldplay - Music of the Spheres Tour"
                  value={formData.event_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de l'événement *</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure de l'événement *</Label>
                  <Input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_time: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Type d'événement</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="sport">Événement sportif</SelectItem>
                    <SelectItem value="theatre">Théâtre / Spectacle</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Lieu de l&apos;événement *</Label>
                <Input
                  placeholder="Ex: Centre Bell, Montréal"
                  value={formData.venue}
                  onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  required
                />
                <p className="text-[11px] text-gray-500">
                  Requis même pour une admission générale.
                </p>
              </div>
            </div>

            {/* Ticket Information */}
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Ticket className="h-5 w-5 text-brand-600" />
                Informations sur les billets
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de billets *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.ticket_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_count: e.target.value }))}
                    onWheel={(e) => e.target.blur()}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prix total *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CAD">CAD $</SelectItem>
                        <SelectItem value="USD">USD $</SelectItem>
                        <SelectItem value="EUR">EUR €</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="150.00"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      onWheel={(e) => e.target.blur()}
                      required
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-base">Type de billet *</Label>
                <RadioGroup
                  value={formData.seat_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, seat_type: value }))}
                  className="space-y-2"
                >
                  <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${formData.seat_type === 'admission_generale' ? 'border-brand-500 bg-brand-50' : 'hover:bg-gray-50'}`}>
                    <RadioGroupItem value="admission_generale" id="admission_generale" />
                    <Label htmlFor="admission_generale" className="cursor-pointer flex-1">
                      Admission générale
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${formData.seat_type === 'siege_assigne' ? 'border-brand-500 bg-brand-50' : 'hover:bg-gray-50'}`}>
                    <RadioGroupItem value="siege_assigne" id="siege_assigne" />
                    <Label htmlFor="siege_assigne" className="cursor-pointer flex-1">
                      Siège assigné
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${formData.seat_type === 'zone_speciale' ? 'border-brand-500 bg-brand-50' : 'hover:bg-gray-50'}`}>
                    <RadioGroupItem value="zone_speciale" id="zone_speciale" />
                    <Label htmlFor="zone_speciale" className="cursor-pointer flex-1">
                      <span>Zone spéciale</span>
                      <span className="text-xs text-gray-500 ml-1">(Loge, Or, Argent, Terrasse, etc.)</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Siège assigné: per-ticket details */}
              {formData.seat_type === "siege_assigne" && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                  <Label className="text-sm font-semibold text-gray-700">
                    Détails par billet ({parseInt(formData.ticket_count) || 1} billet{(parseInt(formData.ticket_count) || 1) > 1 ? 's' : ''})
                  </Label>
                  <p className="text-xs text-gray-500">Remplissez uniquement les champs qui apparaissent sur vos billets</p>
                  {ticketDetails.map((ticket, index) => (
                    <div key={index} className="grid grid-cols-4 gap-2 p-3 bg-white rounded-lg border">
                      <div className="col-span-4 text-xs font-medium text-brand-600 mb-1">
                        Billet {index + 1}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Niveau</Label>
                        <Input
                          placeholder="Ex: 100"
                          value={ticket.level || ''}
                          onChange={(e) => {
                            const updated = [...ticketDetails];
                            updated[index] = { ...updated[index], level: e.target.value };
                            setTicketDetails(updated);
                          }}
                          data-testid={`ticket-${index}-level`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Section</Label>
                        <Input
                          placeholder="Ex: 108"
                          value={ticket.section}
                          onChange={(e) => {
                            const updated = [...ticketDetails];
                            updated[index] = { ...updated[index], section: e.target.value };
                            setTicketDetails(updated);
                          }}
                          data-testid={`ticket-${index}-section`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Rangée</Label>
                        <Input
                          placeholder="Ex: A"
                          value={ticket.row}
                          onChange={(e) => {
                            const updated = [...ticketDetails];
                            updated[index] = { ...updated[index], row: e.target.value };
                            setTicketDetails(updated);
                          }}
                          data-testid={`ticket-${index}-row`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Siège</Label>
                        <Input
                          placeholder="Ex: 12"
                          value={ticket.seat}
                          onChange={(e) => {
                            const updated = [...ticketDetails];
                            updated[index] = { ...updated[index], seat: e.target.value };
                            setTicketDetails(updated);
                          }}
                          data-testid={`ticket-${index}-seat`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Zone spéciale: details */}
              {formData.seat_type === "zone_speciale" && (
                <div className="space-y-2">
                  <Label>Précisez la zone spéciale *</Label>
                  <Input
                    placeholder="Ex: Loge VIP, Section Or, Terrasse..."
                    value={formData.special_section_details}
                    onChange={(e) => setFormData(prev => ({ ...prev, special_section_details: e.target.value }))}
                    data-testid="special-zone-details"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Plateforme des billets</Label>
                <Select
                  value={formData.ticket_platform}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, ticket_platform: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    {platforms.map((platform) => (
                      <SelectItem key={platform.name} value={platform.name}>
                        {platform.icon} {platform.display_name || platform.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Autre">❓ Autre</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Autre plateforme - input field */}
                {formData.ticket_platform === "Autre" && (
                  <div className="mt-2 space-y-2">
                    <Input
                      placeholder="Nom de la plateforme (ex: See Tickets, Eventbrite, etc.)"
                      value={formData.other_platform_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, other_platform_name: e.target.value }))}
                      data-testid="other-platform-name"
                    />
                    <p className="text-xs text-gray-500">
                      ℹ️ Cette information sera transmise à notre équipe pour étudier l'ajout de cette plateforme.
                    </p>
                  </div>
                )}
                
                {/* Suggérer une nouvelle billeterie */}
                <button
                  type="button"
                  onClick={() => window.location.href = '/contact?category=nouvelle_billeterie'}
                  className="text-xs text-[#2eb382] hover:text-[#269970] underline mt-1"
                >
                  Votre plateforme n'est pas listée? Suggérez son ajout →
                </button>
              </div>
            </div>

            {/* Promo Code Section */}
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Code promo
              </h3>

              {/* Show user's personal promo code if available */}
              {userPromoCode && !promoCodeResult?.valid && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-green-700 text-sm">🎉 Vous avez un code promo disponible!</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={applyUserPromoCode}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="apply-my-promo-btn"
                    >
                      Appliquer {userPromoCode.promo_code}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Avez-vous un code promo?</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Entrez votre code promo"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoCodeResult(null); // Reset validation on change
                    }}
                    className={promoCodeResult?.valid ? "border-green-500" : promoCodeResult?.valid === false ? "border-red-500" : ""}
                    data-testid="promo-code-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => validatePromoCode(promoCode)}
                    disabled={!promoCode || promoCodeValidating || !formData.price}
                    data-testid="validate-promo-btn"
                  >
                    {promoCodeValidating ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      "Valider"
                    )}
                  </Button>
                </div>
                {promoCodeResult && (
                  <div className={`text-sm ${promoCodeResult.valid ? "text-green-600" : "text-red-600"}`}>
                    {promoCodeResult.valid ? "✅ " : "❌ "}{promoCodeResult.message}
                  </div>
                )}
                {promoCodeResult?.valid && promoCodeResult.fee_breakdown && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Frais de base:</span>
                      <span className="line-through">{promoCodeResult.fee_breakdown.base_fee?.toFixed(2)}$</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Réduction ({promoCodeResult.discount_rate}%):</span>
                      <span>-{promoCodeResult.fee_breakdown.discount_amount?.toFixed(2)}$</span>
                    </div>
                    <div className="flex justify-between font-bold text-green-700">
                      <span>Nouveau total des frais:</span>
                      <span>{promoCodeResult.fee_breakdown.final_fee?.toFixed(2)}$</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fee Information */}
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-brand-600" />
                Répartition des frais
              </h3>

              <div className="space-y-2">
                <Label>Qui paie les frais de transfert sécurisé ({getFeeDisplayText()})?</Label>
                <Select
                  value={formData.fee_payer}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, fee_payer: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="buyer">L'acheteur</SelectItem>
                    <SelectItem value="seller">Le vendeur</SelectItem>
                    <SelectItem value="split">Partagé (50/50)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.price && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Prix des billets:</span>
                    <span className="font-medium">{parseFloat(formData.price).toFixed(2)} {fees.currencyDisplay} {fees.currencySymbol}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Frais de transfert sécurisé:</span>
                    <span>{fees.fee.toFixed(2)} {fees.currencyDisplay} {fees.currencySymbol}</span>
                  </div>
                  <div className="text-xs text-gray-500 italic">
                    {getFeeTierText()}
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-green-700">
                    <span>L'acheteur paie:</span>
                    <span className="font-bold">{fees.buyerPays.toFixed(2)} {fees.currencyDisplay} {fees.currencySymbol}</span>
                  </div>
                  <div className="flex justify-between text-brand-700">
                    <span>Le vendeur reçoit:</span>
                    <span className="font-bold">{fees.sellerReceives.toFixed(2)} {fees.currencyDisplay} {fees.currencySymbol}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Info about next steps */}
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Prochaines étapes:</strong> Après l'envoi de ce formulaire, l'autre partie recevra une notification pour confirmer les informations. Vous n'aurez pas à reconfirmer puisque vous avez rempli le formulaire.
              </AlertDescription>
            </Alert>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={onCancel}
                >
                  Annuler
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1 bg-brand-600 hover:bg-brand-700 h-12 text-base"
                disabled={isLoading || !sellerInfo?.found || !buyerInfo?.found}
                data-testid="create-transfer-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    Créer la demande de transfert
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
          
          {/* Report bug link */}
          <div className="flex justify-center pt-4 mt-2 border-t border-gray-100">
            <ReportBugLink variant="muted" />
          </div>
        </div>
      )}
      </div>
  );
};

export default TransferRequestForm;
